import type { ResumeDto } from "@reactive-resume/dto";
import type { ResumeData } from "@reactive-resume/schema";
import type { Template } from "@reactive-resume/utils";
import { useMutation } from "@tanstack/react-query";

import { applyTemplateThemeToData } from "@/client/constants/template-presets";
import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

import type { LayoutPage } from "../ocr/extract";

export const analyzeResumeLayout = async (pages: LayoutPage[]) => {
  const response = await axios.post<{
    data: ResumeData;
  }>("/resume/import-flow/analyze-layout", {
    pages,
  });

  return response.data.data;
};

type FinalizeResumeImport = {
  title?: string;
  template: Template;
  data: ResumeData;
};

export const finalizeResumeImport = async (payload: FinalizeResumeImport) => {
  /*
   * La palette officielle est
   * appliquée avant l’envoi.
   *
   * Le backend reçoit donc déjà
   * une structure ResumeData
   * complète et cohérente.
   */
  const themedData = applyTemplateThemeToData(payload.data, payload.template);

  const response = await axios.post<ResumeDto>("/resume/import-flow/finalize", {
    ...payload,
    data: themedData,
    visibility: "private",
  });

  return response.data;
};

export const useAnalyzeResumeLayout = () =>
  useMutation({
    mutationFn: analyzeResumeLayout,
  });

export const useFinalizeResumeImport = () =>
  useMutation({
    mutationFn: finalizeResumeImport,

    onSuccess: (resume) => {
      queryClient.setQueryData(
        [
          "resume",
          {
            id: resume.id,
          },
        ],
        resume,
      );

      queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) =>
        cache ? [resume, ...cache] : [resume],
      );
    },
  });
